import type { CSSProperties } from 'react'
import { forwardRef, useState, MouseEvent, ButtonHTMLAttributes } from 'react'
import './Button.css'
import { ClassName, cx } from '@/utils/cx'
import { onKeyDownHandler } from '@/methods/keyboardAccessibility'
import { SignDocumentHintTooltip } from './SignDocumentHintTooltip'

interface ButtonProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick' | 'className' | 'style'> {
  id?: string
  type?: 'button' | 'submit' | 'reset'
  children: React.ReactNode
  className?: ClassName
  style?: CSSProperties
  disabled?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (props?: any) => any
  animatable?: boolean
  tabIndex?: number
  buttonText?: string
  showCheckboxTooltip?: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onKeyDown?: (props: any) => any
  dataTestid?: string
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      id,
      type,
      children,
      className,
      style,
      disabled,
      onClick,
      animatable,
      tabIndex,
      buttonText,
      onKeyDown,
      showCheckboxTooltip,
      dataTestid,
      ...rest
    }: ButtonProps,
    ref
  ) => {
    const [showTooltip, setShowTooltip] = useState(false)

    const handleOnClick = (e: MouseEvent<HTMLButtonElement>) => {
      onClick && onClick(e)
    }

    const handleOnKeyDown = () => {
      onClick && onClick()
    }

    return (
      <button
        id={id}
        type={type || 'button'}
        ref={ref}
        disabled={disabled}
        onClick={(e) => handleOnClick(e)}
        className={cx(
          'button',
          'onboarding-button',
          animatable && 'button__animatable',
          buttonText && 'button__has-button-text',
          className
        )}
        style={{
          ...style,
          ...(buttonText ? ({ '--btn-button-text': `"${buttonText}"` } as CSSProperties) : {}),
        }}
        tabIndex={tabIndex || 0}
        onKeyDown={onKeyDown ? (event) => onKeyDownHandler(event, handleOnKeyDown) : undefined}
        onMouseOver={() => setShowTooltip(true)}
        onMouseOut={() => setShowTooltip(false)}
        data-testid={dataTestid}
        {...rest}
      >
        {showCheckboxTooltip && <SignDocumentHintTooltip show={showTooltip} />}
        {children}
      </button>
    )
  }
)
